import { ApolloClient } from '@apollo/client';
import { useApolloClient, useQuery } from '@apollo/client/react';
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

import { useQueryErrorToast } from './useQueryErrorToast';
import { fetchAllPages } from './utils';
import { Kitchen, ListFoodsQuery, ListFoodsQueryVariables } from '../libs';
import { LIST_FOODS_QUERY } from '../libs/graphQL';

type UseListPaginatedFoodsQueryProps = {
  kitchen: Kitchen;
  date: string;
  shift?: string;
  brand?: string;
  limit?: number;
  next?: string;
  fetchAll?: boolean;
};

type FoodsDay = Omit<UseListPaginatedFoodsQueryProps, 'next' | 'fetchAll'>;

const toVariables = ({ kitchen, date, shift, brand, limit = 500 }: FoodsDay) => ({ kitchen, date, shift, brand, limit });

// A plain refetch re-requests page one only, and the listFoods merge policy then replaces the full list with it.
/** Replaces the cached day with every page from the API; `onlyIfLoaded` skips a day no screen has fetched yet. */
export const refetchAllFoodPages = async (
  client: ApolloClient,
  day: FoodsDay,
  { onlyIfLoaded = false }: { onlyIfLoaded?: boolean } = {}
) => {
  const variables = toVariables(day);
  if (onlyIfLoaded && !client.readQuery({ query: LIST_FOODS_QUERY, variables })) return;

  try {
    const allFoods = await fetchAllPages(async (cursor) => {
      const res = await client.query<ListFoodsQuery, ListFoodsQueryVariables>({
        query: LIST_FOODS_QUERY,
        variables: { ...variables, next: cursor },
        fetchPolicy: 'no-cache'
      });
      return { items: res.data?.listFoods?.data ?? [], next: res.data?.listFoods?.next };
    });

    client.writeQuery<ListFoodsQuery, ListFoodsQueryVariables>({
      query: LIST_FOODS_QUERY,
      variables,
      data: { listFoods: { __typename: 'ListFoodsResponse', data: allFoods, next: null } } as unknown as ListFoodsQuery
    });
  } catch (error) {
    toast.error(error instanceof Error ? error.message : 'Failed to refresh foods');
  }
};

const useListPaginatedFoodsQuery = ({
  kitchen,
  date,
  shift,
  brand,
  limit = 500,
  next,
  fetchAll = false
}: UseListPaginatedFoodsQueryProps) => {
  const client = useApolloClient();
  const { data, error, loading, refetch, fetchMore } = useQuery<ListFoodsQuery, ListFoodsQueryVariables>(LIST_FOODS_QUERY, {
    variables: toVariables({ kitchen, date, shift, brand, limit })
  });

  useQueryErrorToast(error);

  const foods = data?.listFoods?.data ?? [];
  const nextToken = data?.listFoods?.next ?? next;

  const seen = useRef(new Set<string>());
  useEffect(() => {
    if (!fetchAll || !nextToken) return;
    if (seen.current.has(nextToken)) return;

    seen.current.add(nextToken);
    fetchMore({ variables: { next: nextToken } }).catch((error) => {
      toast.error(error.message || 'Error fetching additional foods');
    });
  }, [fetchAll, nextToken, kitchen, date, shift, brand, fetchMore]);

  useEffect(() => {
    seen.current.clear();
  }, [kitchen, date, shift, brand]);

  const refetchAllPages = useCallback(async () => {
    await refetchAllFoodPages(client, { kitchen, date, shift, brand, limit });
    seen.current.clear();
  }, [client, kitchen, date, shift, brand, limit]);

  return {
    foodsLoading: loading,
    foods,
    refetchFoods: fetchAll ? refetchAllPages : refetch,
    hasNext: !!nextToken
  };
};

export default useListPaginatedFoodsQuery;
